import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    AlertCircle,
    ArrowRight,
    CheckCircle2,
    CreditCard,
    DollarSign,
    FileText,
    Loader2,
    Wrench,
} from "lucide-react";
import { PartnerAPI } from "@/lib/partnerApi";
import { formatApiError } from "@/context/PartnerAuthContext";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

export default function RequestsPanel() {
    const [categories, setCategories] = useState(null);
    const [requests, setRequests] = useState([]);
    const [reports, setReports] = useState([]);

    // Request a change
    const [reqCategory, setReqCategory] = useState("typo_fix");
    const [reqDesc, setReqDesc] = useState("");
    const [submittingReq, setSubmittingReq] = useState(false);

    // Report a problem
    const [problemDesc, setProblemDesc] = useState("");
    const [problemSev, setProblemSev] = useState("normal");
    const [submittingProb, setSubmittingProb] = useState(false);

    const load = async () => {
        try {
            const [c, r, p] = await Promise.all([
                PartnerAPI.paidCategories(),
                PartnerAPI.changeRequests(),
                PartnerAPI.problemReports(),
            ]);
            setCategories(c.categories);
            setRequests(r.requests || []);
            setReports(p.reports || []);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (!categories) {
        return (
            <div className="py-10 text-center">
                <Loader2 size={24} className="animate-spin inline text-gold" />
            </div>
        );
    }

    const selected = categories[reqCategory] || {};

    const submitRequest = async () => {
        if (reqDesc.trim().length < 5) {
            toast.error("Describe your change in a bit more detail");
            return;
        }
        setSubmittingReq(true);
        try {
            const data = await PartnerAPI.submitChangeRequest({
                category: reqCategory,
                description: reqDesc,
            });
            if (data.payment_url) {
                toast.success("Opening secure Stripe checkout…");
                window.location.href = data.payment_url;
                return;
            }
            toast.success("Free request submitted to the EIGOO team");
            setReqDesc("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmittingReq(false);
        }
    };

    const submitProblem = async () => {
        if (problemDesc.trim().length < 5) {
            toast.error("Describe the problem in a bit more detail");
            return;
        }
        setSubmittingProb(true);
        try {
            await PartnerAPI.reportProblem({
                description: problemDesc,
                severity: problemSev,
            });
            toast.success("Problem reported · the EIGOO team is notified");
            setProblemDesc("");
            setProblemSev("normal");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmittingProb(false);
        }
    };

    return (
        <section className="space-y-10" data-testid="partner-requests-panel">
            <header>
                <span className="divider-rule text-gold">
                    <Wrench size={12} /> Requests &amp; Reports
                </span>
                <h2 className="font-serif text-3xl text-cream mt-5">
                    Need something done?
                </h2>
                <p className="text-cream/60 text-sm mt-1 max-w-2xl">
                    Request a paid change (billed directly to you via Stripe)
                    or report a problem to the EIGOO team.
                </p>
            </header>

            {/* Request a change */}
            <div
                className="p-6 bg-cream/5 border border-cream/10 rounded-sm backdrop-blur space-y-4"
                data-testid="partner-request-change-form"
            >
                <h3 className="font-serif text-xl text-cream inline-flex items-center gap-2">
                    <FileText size={16} className="text-gold" /> Request a Change
                </h3>
                <label className="block">
                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-cream/60">
                        Category
                    </span>
                    <select
                        value={reqCategory}
                        onChange={(e) => setReqCategory(e.target.value)}
                        data-testid="partner-request-category"
                        className="mt-1.5 w-full px-4 py-3 bg-cream/5 border border-cream/15 rounded-sm text-cream focus:outline-none focus:border-gold"
                    >
                        {Object.entries(categories).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v.label} · {v.cost === 0 ? "Free" : `$${v.cost}`}
                            </option>
                        ))}
                    </select>
                </label>

                <div
                    className={`p-3 rounded-sm border inline-flex items-center gap-2 text-sm ${
                        selected.cost === 0
                            ? "bg-cream/5 border-cream/15 text-cream/80"
                            : "bg-gold/10 border-gold text-cream"
                    }`}
                    data-testid="partner-request-cost-estimate"
                >
                    {selected.cost === 0 ? (
                        <CheckCircle2 size={14} className="text-gold" />
                    ) : (
                        <DollarSign size={14} className="text-gold" />
                    )}
                    {selected.cost === 0 ? (
                        <span>Free · routes straight to the EIGOO team.</span>
                    ) : (
                        <span>
                            Cost estimate:{" "}
                            <strong className="text-gold">
                                ${selected.cost}.00 USD
                            </strong>{" "}
                            · you'll be redirected to secure Stripe checkout.
                        </span>
                    )}
                </div>

                <label className="block">
                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-cream/60">
                        What do you want changed?
                    </span>
                    <textarea
                        rows={4}
                        value={reqDesc}
                        onChange={(e) => setReqDesc(e.target.value)}
                        data-testid="partner-request-description"
                        placeholder="Describe the change you want. The more detail, the faster the team can scope it."
                        className="mt-1.5 w-full px-4 py-3 bg-cream/5 border border-cream/15 rounded-sm text-cream focus:outline-none focus:border-gold"
                    />
                </label>
                <button
                    type="button"
                    onClick={submitRequest}
                    disabled={submittingReq}
                    data-testid="partner-request-submit"
                    className="btn-pill btn-gold"
                >
                    {submittingReq ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : selected.cost === 0 ? (
                        <>
                            Submit <ArrowRight size={14} />
                        </>
                    ) : (
                        <>
                            <CreditCard size={14} /> Top Up &amp; Submit — $
                            {selected.cost}
                        </>
                    )}
                </button>
            </div>

            {requests.length > 0 && (
                <div>
                    <h3 className="font-serif text-xl text-cream mb-3">
                        Your Requests
                    </h3>
                    <ul className="space-y-2">
                        {requests.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`partner-request-row-${r.id}`}
                                className="p-4 bg-cream/5 border border-cream/10 rounded-sm text-sm"
                            >
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-gold">
                                        {r.category_label}
                                    </span>
                                    <span className="text-[0.6rem] tracking-[0.22em] uppercase bg-cream/10 text-cream/70 px-2 py-0.5 rounded-full">
                                        {r.status}
                                    </span>
                                    {r.cost > 0 && (
                                        <span className="text-[0.6rem] tracking-[0.22em] uppercase bg-gold/20 text-gold px-2 py-0.5 rounded-full">
                                            ${r.cost} · {r.payment_status}
                                        </span>
                                    )}
                                </div>
                                <p className="text-cream mt-2 line-clamp-2">
                                    {r.description}
                                </p>
                                <p className="text-cream/50 text-xs mt-1">
                                    {fmtDate(r.created_at)}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Report a Problem */}
            <div
                className="p-6 bg-cream/5 border border-cream/10 rounded-sm backdrop-blur space-y-4"
                data-testid="partner-report-problem-form"
            >
                <h3 className="font-serif text-xl text-cream inline-flex items-center gap-2">
                    <AlertCircle size={16} className="text-gold" /> Report a Problem
                </h3>
                <div className="grid sm:grid-cols-3 gap-3">
                    {["low", "normal", "urgent"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setProblemSev(s)}
                            data-testid={`partner-problem-sev-${s}`}
                            className={`py-2 px-3 rounded-sm text-[0.65rem] tracking-[0.28em] uppercase transition-colors ${
                                problemSev === s
                                    ? "bg-gold text-forest-deep"
                                    : "bg-cream/5 border border-cream/15 text-cream/70 hover:text-cream"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <label className="block">
                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-cream/60">
                        What's wrong?
                    </span>
                    <textarea
                        rows={4}
                        value={problemDesc}
                        onChange={(e) => setProblemDesc(e.target.value)}
                        data-testid="partner-problem-description"
                        placeholder="Describe the issue — page, step, what you saw vs. expected."
                        className="mt-1.5 w-full px-4 py-3 bg-cream/5 border border-cream/15 rounded-sm text-cream focus:outline-none focus:border-gold"
                    />
                </label>
                <button
                    type="button"
                    onClick={submitProblem}
                    disabled={submittingProb}
                    data-testid="partner-problem-submit"
                    className="btn-pill btn-primary"
                >
                    {submittingProb ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <>
                            Send to EIGOO <ArrowRight size={14} />
                        </>
                    )}
                </button>
            </div>

            {reports.length > 0 && (
                <div>
                    <h3 className="font-serif text-xl text-cream mb-3">
                        Reported Problems
                    </h3>
                    <ul className="space-y-2">
                        {reports.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`partner-problem-row-${r.id}`}
                                className="p-4 bg-cream/5 border border-cream/10 rounded-sm text-sm"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-gold">
                                        {r.severity}
                                    </span>
                                    <span className="text-[0.6rem] tracking-[0.22em] uppercase bg-cream/10 text-cream/70 px-2 py-0.5 rounded-full">
                                        {r.status}
                                    </span>
                                </div>
                                <p className="text-cream mt-2 line-clamp-2">
                                    {r.description}
                                </p>
                                <p className="text-cream/50 text-xs mt-1">
                                    {fmtDate(r.created_at)}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
